import * as Sentry from '@sentry/react';
import URI from 'jsuri';

import config from 'js/app/config';
import redirect from 'js/lib/coursera.redirect';

import {
  AUTHENTICATION_FORM_VARIANTS,
  AUTHENTICATION_MODAL_MODES,
  type AUTHENTICATION_MODAL_MODES_KEY,
  type AUTH_MODES_WITH_SINGLE_PAGE,
  DEFAULT_THIRD_PARTY_AUTH_SERVICES,
  type LoginMethodType,
} from 'bundles/authentication-common/constants';

const navigateToFullPageAuth = (
  authMode: (typeof AUTH_MODES_WITH_SINGLE_PAGE)[keyof typeof AUTH_MODES_WITH_SINGLE_PAGE],
  redirectTo: string
) => {
  const authPageUrlLocation = new URI().setPath(`/${authMode}`).addQueryParam('redirectTo', redirectTo).toString();
  redirect.setLocation(authPageUrlLocation);
};

function toValidRedirect(redirectTo?: unknown) {
  if (!!redirectTo && typeof redirectTo === 'string') {
    let redirectUrl;
    try {
      redirectUrl = new URL(redirectTo);
    } catch (e) {
      // Allow only relative paths starting with `&`, `?`, or `/`
      if (redirectTo.match(/^[&?/]/)) {
        return redirectTo;
      }
      Sentry.captureException(new Error('unexpected redirect URL - malformed redirect URL'), {
        extra: { error: e, redirectTo },
      });
      return config.url.base;
    }
    const redirectDomain = redirectUrl.host;
    const redirectProtocol = redirectUrl.protocol;

    // Prevent non-HTTP/HTTPS schemes like `javascript:`
    if (!['http:', 'https:'].includes(redirectProtocol)) {
      Sentry.captureException(new Error('unexpected redirect URL - invalid protocol'), { extra: { redirectTo } });
      return config.url.base;
    }

    const courseraDomain = new URL(config.url.base).host.replace(/(https:\/\/)?(www.)?/, '');
    if (redirectDomain?.endsWith(courseraDomain)) {
      return redirectTo;
    }
    Sentry.captureException(new Error('unexpected redirect URL - untrusted domain'), { extra: { redirectTo } });
  }
  return config.url.base;
}

type AuthModeResult = {
  mode: AUTHENTICATION_MODAL_MODES_KEY;
  navigationOptions: Record<string, string>;
  shouldSwitchFormsByQueryParam: boolean;
};

function loginMethodsToAuthModeWithMetadata({
  loginMethods,
  email,
}: {
  loginMethods: Array<LoginMethodType>;
  email: string;
}): AuthModeResult {
  const hasThirdPartyAuth = (methods: Array<LoginMethodType>) =>
    methods.some((method) => DEFAULT_THIRD_PARTY_AUTH_SERVICES.includes(method));

  const getThirdPartyAuthService = (methods: Array<LoginMethodType>) =>
    methods.find((method) => DEFAULT_THIRD_PARTY_AUTH_SERVICES.includes(method));

  const createAuthResult = (
    mode: AUTHENTICATION_MODAL_MODES_KEY,
    formVariant: string,
    thirdPartyAuthService?: string
  ): AuthModeResult => ({
    mode,
    navigationOptions: {
      defaultEmail: email,
      formVariant,
      ...(thirdPartyAuthService && { thirdPartyAuthService }),
    },
    shouldSwitchFormsByQueryParam: false,
  });

  // Generic authentication (highest priority).
  // 'deprecated' is returned by the backend for login types that are no longer supported.
  // We fall back to the generic login form so these users can still authenticate.
  if (loginMethods.includes('generic') || loginMethods.includes('deprecated')) {
    return createAuthResult(AUTHENTICATION_MODAL_MODES.LOGINCONTINUE, AUTHENTICATION_FORM_VARIANTS.GENERIC);
  }

  // Multiple methods including social auth
  if (loginMethods.length > 1 && hasThirdPartyAuth(loginMethods)) {
    return createAuthResult(
      AUTHENTICATION_MODAL_MODES.LOGINCONTINUE,
      AUTHENTICATION_FORM_VARIANTS.GENERIC,
      getThirdPartyAuthService(loginMethods)
    );
  }

  // Single password method
  if (loginMethods.length === 1 && loginMethods[0] === 'password') {
    return createAuthResult(AUTHENTICATION_MODAL_MODES.LOGINCONTINUE, AUTHENTICATION_FORM_VARIANTS.PASSWORD);
  }

  // Single social auth method
  if (loginMethods.length === 1 && hasThirdPartyAuth(loginMethods)) {
    return createAuthResult(
      AUTHENTICATION_MODAL_MODES.LOGINCONTINUE,
      AUTHENTICATION_FORM_VARIANTS.SOCIALSSO,
      getThirdPartyAuthService(loginMethods)
    );
  }

  // Default fallback - signup
  return createAuthResult(AUTHENTICATION_MODAL_MODES.SIGNUPCONTINUE, AUTHENTICATION_FORM_VARIANTS.PASSWORD);
}

const getAbsoluteRedirectToURL = (redirectTo: string) => {
  let redirectToURL: string | undefined;
  let validRedirectTo: string | undefined;

  if (redirectTo) {
    try {
      validRedirectTo = toValidRedirect(redirectTo);
      redirectToURL = new URL(validRedirectTo, config.url.base).toString();
    } catch (err) {
      Sentry.captureException(new Error('Unexpected redirect URL - malformed redirect URL'), {
        extra: { error: err, validRedirectTo },
      });
      redirectToURL = undefined;
    }
  }

  return redirectToURL;
};

export { navigateToFullPageAuth, toValidRedirect, loginMethodsToAuthModeWithMetadata, getAbsoluteRedirectToURL };
